import { read } from 'xlsx';
import { extractData, extractHeader } from './sheet-utils';
import { Sheet, WorkBook } from './types';

const EmptyWorkBook: WorkBook = { SheetNames: [], GetSheet: () => null!, fileHash: null! };

export const ACCEPTED_FORMATS = {
	'text/csv': ['.csv'],
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
	'text/*': ['.brf']
};

export async function parseFile<T extends File>(file: T) {
	if (file.name.match(/\.brf$/i)) {
		return await parseBrf(file);
	}

	return await parseStandard(file);
}

export const WORKBOOK_READ_DATE_FORMAT = 'yyyy-mm-dd';

export async function parseStandard(file: File, readDateFormat = WORKBOOK_READ_DATE_FORMAT): Promise<WorkBook> {
	const buffer = await file.arrayBuffer();
	const hash = await digestBuffer(buffer);
	// This is extremely stupid. Surely the xlsx file has somewhere in its metadata information about the date format
	// to use. Why do we have to tell it????
	const workbook = read(buffer, { dateNF: readDateFormat, raw: true });
	const workbookAdapter = {
		fileHash: hash,
		SheetNames: workbook.SheetNames,
		GetSheet(name: string): Sheet {
			const s = workbook.Sheets[name];
			return {
				extractHeader: (has: boolean, skip?: number) => extractHeader(s, has, skip),
				extractData: x => extractData({ ws: s, ...x })
			};
		}
	};
	return workbookAdapter;
}

function makeIsoDate(date: string) {
	if (date.length !== 8) return date;
	return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6)}`;
}

export async function digestBuffer(arrayBuffer: ArrayBuffer) {
	const hashBuffer = await window.crypto.subtle.digest('SHA-256', arrayBuffer); // hash the message
	const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
	const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
	return hashHex;
}

/*
https://www.nab.com.au/content/dam/nabconnectcontent/file-formats/nab-connect-consolidated-file-format-specification_v0.05-pdf.pdf
Section 5.2 - BPAY Remittance File
*/
export async function parseBrf(first: File): Promise<WorkBook> {
	const text = await first.text();

	// parse out the brf data
	const raw = text.split(/\r?\n/);
	// first row should have a header row
	if (raw.length < 3) return EmptyWorkBook;

	const data = raw
		.filter(a => a.match(/^50/))
		.map(a => {
			// to 24;  05 = payment, 15 = error correction, 25 = reversal (we need to handle the se cases)
			const instruction = a.substring(32, 32 + 2);

			return {
				'Record Type': a.substring(0, 2).trim(),
				'Biller Code': a.substring(2, 2 + 10).trim(), // 12
				CRN: a.substring(12, 12 + 20).trimEnd(), // 32
				Success: instruction === '05' ? 'Y' : 'N', // this is documented here https://developer.bpaygroup.com.au/docs/bpay-payments-payment-submission-sandbox/1/types/BulkPaymentSubmissionRequest
				Refund: instruction === '25' ? 'Y' : 'N',
				'BPay Instruction': instruction,
				'Transaction Reference': a.substring(34, 34 + 21).trim(), // 55
				'Original Reference': a.substring(55, 65 + 21).trim(), // 86
				ECR: a.substring(76, 76 + 3), // 79
				'Amount Cents': a.substring(79, 79 + 12), // 101
				'Payment Date': makeIsoDate(a.substring(91, 91 + 8)), // 99
				'Payment Time': a.substring(99, 99 + 6), // 99
				'Settlement Date': makeIsoDate(a.substring(105, 105 + 8)) // 99
			};
		});

	const hash = await digestBuffer(await first.arrayBuffer());

	return {
		SheetNames: ['data'],
		fileHash: hash,
		GetSheet: () => {
			return {
				extractData: () => data,
				extractHeader: () => [
					'Record Type',
					'Biller Code',
					'CRN',
					'BPay Instruction',
					'Transaction Reference',
					'Original Reference',
					'ECR',
					'Amount Cents',
					'Payment Date',
					'Payment Time',
					'Settlement Date',
					'Success',
					'Refund'
				]
			};
		}
	};
}
